import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  applyTenantLogoDataUrlToNode,
  getTenantLogoDataUrl,
  isTenantLogoDataUrl,
  stampTenantLogoOnPdf,
} from '../../services/tenantLogoApi';

const waitForTenantLogo = async (node, timeoutMs = 15000) => {
  if (!node) return;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const pending = node.getAttribute('data-tenant-logo-ready') === '0';
    if (!pending) {
      const imgs = Array.from(node.querySelectorAll('[data-tenant-logo="1"] img'));
      if (!imgs.some((img) => img.src && !img.complete)) return;
    }
    await new Promise((r) => setTimeout(r, 80));
  }
};

/**
 * Same approach as Interior Quotation `exportNodeToPdf`:
 * clone off-screen, rasterize with html2canvas, slice into A4 pages.
 */
export const exportProjectScopeNodeToPdf = async (
  node,
  fileName = 'scope-sheet.pdf'
) => {
  if (!node) throw new Error('No document to export');

  await waitForTenantLogo(node);
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  let logoDataUrl = null;
  const fetchedLogo = await getTenantLogoDataUrl().catch(() => null);
  if (isTenantLogoDataUrl(fetchedLogo)) {
    logoDataUrl = fetchedLogo;
    await applyTenantLogoDataUrlToNode(node, logoDataUrl);
  }

  const docWidthPx = node.scrollWidth || node.offsetWidth || 794;
  const host = document.createElement('div');
  host.setAttribute('data-project-scope-pdf-export', 'true');
  host.style.cssText =
    'position:fixed;left:0;top:0;z-index:-1;pointer-events:none;opacity:0.01;';
  const clone = node.cloneNode(true);
  clone.style.width = `${docWidthPx}px`;
  host.appendChild(clone);
  document.body.appendChild(host);

  try {
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      windowWidth: clone.scrollWidth || docWidthPx,
      logging: false,
      onclone: (clonedDoc) => {
        const exported = clonedDoc.querySelector(
          '[data-project-scope-pdf-export="true"]'
        );
        if (exported) exported.style.opacity = '1';
        clonedDoc.querySelectorAll('[data-tenant-logo="1"] img').forEach((el) => {
          el.style.visibility = 'hidden';
        });
      },
    });

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png', 1.0);

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    stampTenantLogoOnPdf(pdf, node, logoDataUrl, pageWidth);

    pdf.save(fileName);
  } finally {
    host.remove();
  }
};

export const buildScopePdfFileName = (project) => {
  const base =
    String(project?.clientName || project?.name || 'scope-sheet')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'scope-sheet';
  return `${base}-scope-sheet.pdf`;
};
